import {
  MOVE_SHEET_COLUMN,
  MOVE_SHEET_COLUMN_LABEL,
} from '@/modules/move/entities/constants/moveSheetColumn';
import {
  MoveSheet,
  MoveSheetContent,
  MoveSheetMessages,
  MoveSheetMessagesContentDataError, MoveSheetNormalized, MoveSheetNormalizedContent,
} from '@/modules/move/entities/interfaces/moveSheet';
import {
  translateSheetValues,
  translateValidationMessage,
} from '@/modules/move/utils/translations';
import { safeJsonParse } from '@/utils/validations/safeJsonParse';
import { get, lowerCase, capitalize } from 'lodash';
import { MoveSheetColumnKey } from '../entities/types/moveSheetColumn';

export const getErrorMessage = (
  messages: MoveSheetMessages[],
): MoveSheetMessagesContentDataError[] => {
  return messages
    .filter((message) => message.type === 'error')
    .sort(
      (messageA, messageB) =>
        new Date(messageA.createdAt).getTime() - new Date(messageB.createdAt).getTime(),
    )
    .map((message) => get(message, 'content.data.error', ''))
    .pop();
};

export const getContentData = (content: string): { [key in MoveSheetColumnKey]: string | null } => {
  return JSON.parse(content);
};

export const normalizeSheetToList = ({
  contentData,
  messageErrors,
  rowError,
}: {
  contentData: MoveSheetContent;
  rowError?: string | null;
  // Esse conceito de erros da mensagem não deveria existir,
  // pois as mensagens do kafka não deveriam chegar no front.
  // Criamos uma task para ajustarmos isso: HEF-2104.
  messageErrors?: MoveSheetMessagesContentDataError[];
}): MoveSheetNormalizedContent[] => {
  const columnsWithError: MoveSheetNormalizedContent[] = [];
  const parseErrorKey = safeJsonParse(rowError);
  const avaliableErrors = messageErrors?.length ? messageErrors : parseErrorKey;

  Object.entries(contentData)
    .filter(([key]) => key !== 'tipoMovimentacao')
    .forEach(([key, value]) => {
      const column: string =
        MOVE_SHEET_COLUMN_LABEL[key as keyof MoveSheetContent] || capitalize(lowerCase(key));
      const convertedValue = translateSheetValues(key, value);

      const findError = !avaliableErrors
        ? undefined
        : avaliableErrors.find(
          (error: MoveSheetMessagesContentDataError) => error.variable === key,
        );

      const messages = !findError
        ? []
        : Object.entries(findError.messages).map(([errorKey, errorMessage]) => ({
          key: errorKey,
          value: translateValidationMessage(
            errorKey,
            column,
            convertedValue,
            errorMessage as string,
          ),
        }));

      columnsWithError.push({
        key: key as MOVE_SHEET_COLUMN,
        column,
        value: convertedValue,
        hasError: !!findError,
        belongsToLayout: true,
        messages,
      });
    });

  if (avaliableErrors) {
    avaliableErrors.forEach((error: MoveSheetMessagesContentDataError) => {
      if (!(error.variable in contentData) && error.variable !== 'holder') {
        columnsWithError.push({
          key: error.variable as MOVE_SHEET_COLUMN,
          column: capitalize(lowerCase(error.variable)),
          hasError: true,
          beneficiaryName: contentData.nome ? String(contentData.nome) : '',
          beneficiaryCpf: String(contentData.cpf),
          belongsToLayout: false,
          messages: Object.entries(error.messages).map(([errorKey, errorMessage]) => ({
            key: errorKey,
            value: translateValidationMessage(errorKey, error.variable, '', errorMessage),
          })),
        });
      }
    });
  }

  return columnsWithError;
};

export const normalizeSheetLine = (sheetLine: MoveSheet): MoveSheetNormalized => {
  const { line, id, error, messages, content, errorType, fileId } = sheetLine;
  let listContent = undefined;
  let messageWarning: string | undefined = '';
  const data = getContentData(content);
  if (messages) {
    const errors = getErrorMessage(messages);
    messageWarning = errors?.find((error) => error.variable === 'holder')?.messages.error;

    listContent = normalizeSheetToList({
      contentData: data,
      messageErrors: errors,
      rowError: error,
    });
  }

  const lastMessage = messages?.pop();

  return {
    line,
    id,
    fileId,
    hasError: Boolean(error),
    errorType,
    content: listContent,
    originalContent: data,
    messageWarning,
    review: {
      beneficiaryName: data.nome,
      cpf: data.cpf,
      errors: get(lastMessage, 'content.data.error', []).map(
        (message: MoveSheetMessagesContentDataError) => message.messages.error,
      ),
    },
  };
};
